import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import api from '../../services/api'; // Assuming you have an api module to make API requests
import { GET_PHARMACY_TEAM_LIST, CREATE_PHARMACY_USER, DELETE_PHARMACY_USER } from '../../services/urls'; // Assuming you have a URL for fetching teams
import { getPharmacyId } from '../../utils/commonFunctions';
import { getCurrentMonthDateRange } from "../../utils/dateRange";

// API endpoints
const PHARMACY_URL = 'api/v1/pharmacy';
const PHARMACY_USER_URL = 'api/v1/pharmacy/pharmacy-users';

// Initial state
const initialState = {
  pharmacies: [],
  loading: false,
  error: null,
  addPharmacyLoading: false,
  addPharmacyError: null,
  updatePharmacyLoading: false,
  updatePharmacyError: null,
  deletePharmacyLoading: false,
  deletePharmacyError: null,
  teams: [], // Add this if you need team functionality
  teamLoading: false,
  teamError: null,
  quickLoginLoading: false,
  quickLoginError: null,
  quickLoginData: null,
  pharmacyUsers: [],
  pharmacyUsersLoading: false,
  pharmacyUsersError: null,
};

// Memoized selectors
export const selectPharmaciesState = state => state.pharmacies;

export const selectPharmacies = createSelector(
  [selectPharmaciesState],
  (pharmaciesState) => ({
    pharmacies: pharmaciesState?.pharmacies || [],
    loading: pharmaciesState?.loading || false,
    error: pharmaciesState?.error || null,
  })
);

export const selectTeams = createSelector(
  [selectPharmaciesState],
  (pharmaciesState) => ({
    teams: pharmaciesState?.teams || [],
    loading: pharmaciesState?.teamLoading || false,
    error: pharmaciesState?.teamError || null,
  })
);

export const selectQuickLogin = createSelector(
  [selectPharmaciesState],
  (pharmaciesState) => ({
    data: pharmaciesState?.quickLoginData || null,
    loading: pharmaciesState?.quickLoginLoading || false,
    error: pharmaciesState?.quickLoginError || null,
  })
);

// Async thunks
export const fetchPharmacies = createAsyncThunk(
  'pharmacies/fetchPharmacieslist',
  async (dateRange, { rejectWithValue }) => {
    try {
      const range = dateRange?.startDate ? dateRange : getCurrentMonthDateRange();
      const response = await api.get(`${PHARMACY_URL}/list`, {
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
        },
      });
      const pharmacies = Array.isArray(response?.data?.data) ? response?.data?.data : [];
      return pharmacies;
    } catch (error) {
      console.error('API Error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pharmacies');
    }
  }
);

// Admin Quick Login
export const adminQuickLogin = createAsyncThunk(
  'pharmacies/adminQuickLogin',
  async (pharmacyId, { rejectWithValue }) => {
    try {
      console.log('Attempting admin quick login for pharmacy:', pharmacyId);
      const response = await api.post(`${PHARMACY_URL}/${pharmacyId}/admin-quick-login`);
      console.log('Quick login response:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('Quick login error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to perform quick login');
    }
  }
);

// Add team-related thunks if needed
export const fetchTeams = createAsyncThunk(
  'pharmacies/fetchTeams',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(GET_PHARMACY_TEAM_LIST);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch teams');
    }
  }
);

export const deleteTeams = createAsyncThunk(
  'pharmacies/deleteTeams',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${DELETE_PHARMACY_USER}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete team member');
    }
  }
);

// Search pharmacy users
export const searchPharmacyUsers = createAsyncThunk(
  'pharmacies/searchPharmacyUsers',
  async (searchTerm, { rejectWithValue }) => {
    try {
      const response = await api.get(`${PHARMACY_USER_URL}?search=${searchTerm}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to search pharmacy users');
    }
  }
);

// Create pharmacy user
export const createPharmacyUser = createAsyncThunk(
  'pharmacies/createPharmacyUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post(`${PHARMACY_USER_URL}`, userData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create pharmacy user');
    }
  }
);

export const addPharmacy = createAsyncThunk(
  'pharmacies/addPharmacy',
  async (pharmacyData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("pharmacyName", pharmacyData.pharmacyName);
      formData.append("isActive", pharmacyData.isActive);
      
      // Append address fields as a JSON string
      formData.append("address", JSON.stringify(pharmacyData.address));
      
      // Append tax ID
      if (pharmacyData.taxId) {
        formData.append("taxId", pharmacyData.taxId);
      }

      // Append mobile
      if (pharmacyData.mobile) {
        formData.append("mobile", pharmacyData.mobile);
      }
      
      if (pharmacyData.description) {
        formData.append("description", pharmacyData.description);
      }
      
      if (pharmacyData.image) {
        formData.append("image", pharmacyData.image);
      }
      
      // If existing user is selected
      if (pharmacyData.selectedUserId) {
        formData.append("userId", pharmacyData.selectedUserId);
      } 
      // If creating new user
      else if (pharmacyData.user) {
        formData.append("user", JSON.stringify(pharmacyData.user));
      }

      const response = await api.post(PHARMACY_URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add pharmacy');
    }
  }
);

export const updatePharmacy = createAsyncThunk(
  'pharmacies/updatePharmacy',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("pharmacyName", data.pharmacyName);
      formData.append("isActive", data.isActive);
      
      // Append address fields as a JSON string
      formData.append("address", JSON.stringify(data.address));

      // Append mobile
      if (data.mobile) {
        formData.append("mobile", data.mobile);
      }
      
      // Append tax ID
      if (data.taxId) {
        formData.append("taxId", data.taxId);
      }
      
      if (data.description) {
        formData.append("description", data.description);
      }
      
      if (data.image) {
        formData.append("image", data.image);
      }
      
      // If existing user is selected
      if (data.userIds) {
        formData.append("userIds", data.userIds);
      } 
      // If creating new user
      else if (data.createNewUser && data.user) {
        formData.append("user", JSON.stringify(data.user));
      }

      const response = await api.put(`${PHARMACY_URL}/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update pharmacy');
    }
  }
);

export const deletePharmacy = createAsyncThunk(
  'pharmacies/deletePharmacy',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${PHARMACY_URL}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete pharmacy');
    }
  }
);

// Create slice
const pharmaciesSlice = createSlice({
  name: 'pharmacies',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.addPharmacyError = null;
      state.updatePharmacyError = null;
      state.deletePharmacyError = null;
      state.teamError = null;
      state.quickLoginError = null;
    },
    clearQuickLoginData: (state) => {
      state.quickLoginData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPharmacies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPharmacies.fulfilled, (state, action) => {
        state.loading = false;
        state.pharmacies = action.payload;
      })
      .addCase(fetchPharmacies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Team reducers
      .addCase(fetchTeams.pending, (state) => {
        state.teamLoading = true;
        state.teamError = null;
      })
      .addCase(fetchTeams.fulfilled, (state, action) => {
        state.teamLoading = false;
        state.teams = action.payload;
      })
      .addCase(fetchTeams.rejected, (state, action) => {
        state.teamLoading = false;
        state.teamError = action.payload;
      })

      .addCase(deleteTeams.fulfilled, (state, action) => {
        state.teams = state.teams.filter(team => team._id !== action.payload);
      })

      // Add pharmacy
      .addCase(addPharmacy.pending, (state) => {
        state.addPharmacyLoading = true;
        state.addPharmacyError = null;
      })
      .addCase(addPharmacy.fulfilled, (state, action) => {
        state.addPharmacyLoading = false;
        state.pharmacies.push(action.payload);
      })
      .addCase(addPharmacy.rejected, (state, action) => {
        state.addPharmacyLoading = false;
        state.addPharmacyError = action.payload;
      })

      // Update pharmacy
      .addCase(updatePharmacy.pending, (state) => {
        state.updatePharmacyLoading = true;
        state.updatePharmacyError = null;
      })
      .addCase(updatePharmacy.fulfilled, (state, action) => {
        state.updatePharmacyLoading = false;
        const index = state.pharmacies.findIndex(p => p._id === action.payload._id);
        if (index !== -1) {
          state.pharmacies[index] = action.payload;
        }
      })
      .addCase(updatePharmacy.rejected, (state, action) => {
        state.updatePharmacyLoading = false;
        state.updatePharmacyError = action.payload;
      })

      // Delete pharmacy
      .addCase(deletePharmacy.pending, (state) => {
        state.deletePharmacyLoading = true;
        state.deletePharmacyError = null;
      })
      .addCase(deletePharmacy.fulfilled, (state, action) => {
        state.deletePharmacyLoading = false;
        state.pharmacies = state.pharmacies.filter(p => p._id !== action.payload);
      })
      .addCase(deletePharmacy.rejected, (state, action) => {
        state.deletePharmacyLoading = false;
        state.deletePharmacyError = action.payload;
      })

      // Admin Quick Login reducers
      .addCase(adminQuickLogin.pending, (state) => {
        state.quickLoginLoading = true;
        state.quickLoginError = null;
      })
      .addCase(adminQuickLogin.fulfilled, (state, action) => {
        state.quickLoginLoading = false;
        state.quickLoginData = action.payload;
      })
      .addCase(adminQuickLogin.rejected, (state, action) => {
        state.quickLoginLoading = false;
        state.quickLoginError = action.payload;
      })

      // Search pharmacy users
      .addCase(searchPharmacyUsers.pending, (state) => {
        state.pharmacyUsersLoading = true;
        state.pharmacyUsersError = null;
      })
      .addCase(searchPharmacyUsers.fulfilled, (state, action) => {
        state.pharmacyUsersLoading = false;
        state.pharmacyUsers = action.payload;
      })
      .addCase(searchPharmacyUsers.rejected, (state, action) => {
        state.pharmacyUsersLoading = false;
        state.pharmacyUsersError = action.payload;
      })

      // Create pharmacy user
      .addCase(createPharmacyUser.pending, (state) => {
        state.pharmacyUsersLoading = true;
        state.pharmacyUsersError = null;
      })
      .addCase(createPharmacyUser.fulfilled, (state, action) => {
        state.pharmacyUsersLoading = false;
        state.pharmacyUsers.push(action.payload);
      })
      .addCase(createPharmacyUser.rejected, (state, action) => {
        state.pharmacyUsersLoading = false;
        state.pharmacyUsersError = action.payload;
      });
  },
});

export const { clearErrors, clearQuickLoginData } = pharmaciesSlice.actions;
export default pharmaciesSlice.reducer;
